import { ParseResult } from './event-details-parser';

/**
 * Where each field's current value came from. Plan §7.4.
 *
 * default — the seed (starts now, 4 hours, the account name) or empty.
 * voice   — filled by the last voice/typed parse.
 * user    — the host set it by hand. Voice never overwrites these.
 */
export type Origin = 'default' | 'voice' | 'user';
export type FieldKey = 'eventName' | 'eventCategory' | 'hostedBy' | 'start' | 'duration';
export type FilledBy = Record<FieldKey, Origin>;

export const FIELD_KEYS: FieldKey[] = ['eventName', 'eventCategory', 'hostedBy', 'start', 'duration'];

export function freshFilledBy(): FilledBy {
  return { eventName: 'default', eventCategory: 'default', hostedBy: 'default', start: 'default', duration: 'default' };
}

export interface VoicePatch {
  eventName?: string;
  eventCategory?: string;
  hostedBy?: string;
  startDate?: string;
  startTime?: string;
  durationHours?: number;
  /** Fields this patch fills. */
  filled: FieldKey[];
  /** Fields the parse found but left alone because the host had set them. */
  keptYours: FieldKey[];
  /** Fields that came back as guesses, and what was guessed. */
  notes: string[];
  lowConfidence: FieldKey[];
}

/**
 * Decides what a parse may change. Pure, so it can be reasoned about on its
 * own: a field is written only when the parse found it AND the host has not
 * set it by hand. Nothing the parse did not find is touched.
 */
export function mergeParse(result: ParseResult, filledBy: FilledBy): VoicePatch {
  const patch: VoicePatch = { filled: [], keptYours: [], notes: [], lowConfidence: [] };

  const take = (key: FieldKey, apply: () => void, confidence: string, note?: string) => {
    if (filledBy[key] === 'user') {
      patch.keptYours.push(key);
      return;
    }
    apply();
    patch.filled.push(key);
    if (confidence !== 'high') patch.lowConfidence.push(key);
    if (note) patch.notes.push(note);
  };

  if (result.eventName) {
    const p = result.eventName;
    take('eventName', () => (patch.eventName = p.value), p.confidence, p.note);
  }
  if (result.category) {
    const p = result.category;
    take('eventCategory', () => (patch.eventCategory = p.value), p.confidence, p.note);
  }
  if (result.hostedBy) {
    const p = result.hostedBy;
    take('hostedBy', () => (patch.hostedBy = p.value), p.confidence, p.note);
  }
  if (result.start) {
    const p = result.start;
    take('start', () => {
      patch.startDate = p.value.date;
      patch.startTime = p.value.time;
    }, p.confidence, p.note);
  }
  if (result.durationMinutes) {
    const p = result.durationMinutes;
    take('duration', () => (patch.durationHours = p.value / 60), p.confidence, p.note);
  }
  return patch;
}

/** Plain-language names for the partial-result line ("We couldn't catch the start time"). */
export const FIELD_NAMES: Record<FieldKey, string> = {
  eventName: 'the event name',
  eventCategory: "what you're celebrating",
  hostedBy: 'who is hosting',
  start: 'the start time',
  duration: 'how long it runs',
};

/** The fields worth telling the host we missed. Host name is optional, so never. */
export function missedFields(result: ParseResult): FieldKey[] {
  const missed: FieldKey[] = [];
  if (!result.eventName) missed.push('eventName');
  if (!result.category) missed.push('eventCategory');
  if (!result.start) missed.push('start');
  return missed;
}
